import { fetchModels, refreshModels, type ModelInfo } from '../api/models'
import {
  isLeadWindow,
  setModelRuns,
  setModelWindow as pushWindow,
  type LeadWindow,
} from '../map/layers/modelLayer'
import { prefs, setModelWindow as persistWindow } from './prefs.svelte'

/**
 * What the user has chosen *about* the model layers, as opposed to which ones
 * are on. `layers.svelte.ts` owns the stack; this owns the run and the window.
 *
 * Two settings with deliberately different lifetimes:
 *
 * **The run is session-scoped and never persisted.** A run id written to
 * localStorage names a directory retention deletes within hours, so restoring
 * it after a reload would boot the app pointed at a 404 — which degrades to
 * "no model layer" and looks exactly like a broken feature. It resets to
 * *latest* on load. Plan decision 12.
 *
 * **The lead window is persisted**, under `weather.modelWindow`. It is a
 * preference about how far ahead you look, not about a particular run, so it
 * survives a reload cleanly.
 *
 * It also owns the catalog poller, because what the worker has is the third
 * thing a model layer is built out of and it was the one nothing watched.
 */

export const models = $state({
  /** model id -> run segment (`20260918T12Z`), or absent for "latest". */
  run: {} as Record<string, string>,
  window: '12h' as LeadWindow,
  /** Bumped when a poll finds the catalog materially different; see `note`. */
  revision: 0,
  /** The model ids that moved at `revision`, so only their layers rebuild. */
  changed: [] as string[],
  /**
   * Bumped by every completed poll, material or not.
   *
   * Not the same signal as `revision` and deliberately so: `revision` means
   * *redraw the map*, and the status panel needs a weaker one. "The next run
   * is 21Z, due at 22:27" goes stale at a cycle boundary with nothing new on
   * disk — the exact case `fingerprint` reports as unchanged, because
   * rebuilding a CONUS texture for it would be a fetch for nothing.
   */
  polled: 0,
})

export function initModels(): void {
  // Deliberately not restored from anywhere: see the note above.
  models.run = {}
  models.window = isLeadWindow(prefs.modelWindow) ? prefs.modelWindow : '12h'
  models.revision = 0
  models.changed = []
  models.polled = 0
  fingerprints = new Map()
  seeded = false
  push()
}

/**
 * Push into `lib/map`, which is plain TypeScript and imports no runes.
 *
 * A layer reads both when it is *created*, so `MapView` rebuilds the model
 * layers after every change — there is nothing to mutate on a layer whose
 * frame list was fetched at `load()`.
 */
function push(): void {
  pushWindow(models.window)
  setModelRuns(models.run)
}

export function runOverride(modelId: string): string | null {
  return models.run[modelId] ?? null
}

export function setRunOverride(modelId: string, run: string | null): void {
  const next = { ...models.run }
  if (run) next[modelId] = run
  else delete next[modelId]
  models.run = next
  push()
}

export function setLeadWindow(window: LeadWindow): void {
  models.window = window
  persistWindow(window)
  push()
}

/**
 * A cheap identity for everything a model layer would have to be rebuilt for.
 *
 * `models.run` is a `$state` proxy, so it reads back as a different object
 * every time and nothing may compare it by identity — the same trap
 * `layers.svelte.ts` documents for the frame axis.
 */
export function modelSettingsKey(): string {
  const runs = Object.entries(models.run)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${k}=${v}`)
    .join(',')
  return `${models.window}|${runs}`
}

/**
 * How often to ask the catalog what the worker has now.
 *
 * A new *run* appears once a cycle at most — hourly for HRRR and RAP, every
 * six hours for GFS, NAM and NBM — but a run that is still publishing grows a
 * step at a time, and `latest` does not move until it is servable. A minute is
 * fast enough that nobody sits in front of a stale run for long, and slow
 * enough that the directory walk behind `/api/models` stays free.
 */
const POLL_MS = 60_000

/** model id -> everything about it that would change what is drawn. */
let fingerprints = new Map<string, string>()
/** The first catalog seeds rather than reports; see `note`. */
let seeded = false
let timer: ReturnType<typeof setInterval> | null = null
let inFlight = false

/**
 * Everything about one model that would change what is on screen.
 *
 * Deliberately not the whole response body. This is compared once a minute and
 * almost every comparison is equal, so the poll has to be able to say "nothing
 * moved" cheaply and *certainly* — a false positive rebuilds a layer and
 * re-fetches a texture for nothing. What actually moves is the servable run,
 * how far each retained run reaches, and — only when the deployment's settings
 * change under a running page — which fields exist at all.
 */
function fingerprint(info: ModelInfo): string {
  const runs = info.runs
    .map((r) => `${r.run}/${r.steps}/${r.complete ? 1 : 0}/${r.contiguous_lead ?? '-'}`)
    .join(',')
  return `${info.latest ?? '-'}|${info.variables.map((v) => v.id).join('+')}|${runs}`
}

/**
 * Record a catalog and say which models moved.
 *
 * The first one seeds and reports nothing: against an empty map every model is
 * new, and reporting that at boot would rebuild the very layers that were just
 * built out of this catalog.
 */
function note(found: ModelInfo[]): void {
  const next = new Map(found.map((m) => [m.id, fingerprint(m)]))
  if (!seeded) {
    seeded = true
    fingerprints = next
    return
  }
  const changed = [...next].filter(([id, fp]) => fingerprints.get(id) !== fp).map(([id]) => id)
  // A model that has *left* the catalog is a change too: narrowing
  // `WEATHER_MODEL_FIELDS` under a running page makes its layer a wall of
  // 404s, which looks exactly like a quiet day.
  for (const id of fingerprints.keys()) if (!next.has(id)) changed.push(id)
  fingerprints = next
  if (changed.length === 0) return
  models.changed = changed
  models.revision++
}

async function poll(): Promise<void> {
  // `refreshModels` resolves to [] rather than rejecting, so there is nothing
  // to catch; the guard is only against a slow answer overlapping the next
  // tick, which on a phone with no signal is the normal case.
  if (inFlight) return
  inFlight = true
  try {
    note(await refreshModels())
    // After `note`, so a reader watching both sees one consistent catalog
    // rather than a rebuild and a re-read in either order.
    models.polled++
  } finally {
    inFlight = false
  }
}

function startTimer(): void {
  stopTimer()
  // Refused rather than started and immediately cleared, because the app can
  // mount into a background tab: a restored session, or a phone that opened
  // the PWA and locked before it finished.
  if (document.hidden) return
  timer = setInterval(() => void poll(), POLL_MS)
}

function stopTimer(): void {
  if (timer !== null) clearInterval(timer)
  timer = null
}

/**
 * Keep the catalog live for as long as the page is open. Returns a disposer.
 *
 * Model data is the one overlay whose upstream is a worker on this same box,
 * publishing a step every few minutes — and it was the only layer kind with no
 * refresh at all. Every other kind carries a `refreshSeconds` timer of its own
 * (60 s for alerts, 300 s for outlooks); this is that, one level up, because
 * what goes stale first is not a layer's frames but *which run there is to
 * draw*. Until this existed, a tab opened during a cold start showed the last
 * servable run — 13 hours old for GFS, which publishes 3h35m behind its cycle
 * — and stayed there until somebody reloaded.
 *
 * Nothing runs while the tab is hidden, and a tab that becomes visible asks
 * immediately rather than waiting out the interval. That is the case that
 * actually bites: a phone in a pocket for three hours comes back to a
 * three-hour-old catalog, and leaving `setInterval` running would have fired
 * the whole backlog at once for one answer.
 */
export function startCatalogPolling(): () => void {
  // Seed from the catalog everything else is already awaiting. Memoized, so it
  // costs no request of its own, and it is what stops the first real poll from
  // reporting all six models as changed.
  void fetchModels().then(note)

  const onVisibility = () => {
    if (document.hidden) {
      stopTimer()
    } else {
      void poll()
      startTimer()
    }
  }

  startTimer()
  document.addEventListener('visibilitychange', onVisibility)

  return () => {
    stopTimer()
    document.removeEventListener('visibilitychange', onVisibility)
  }
}
